import { Component, OnInit, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';

import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TelecomService } from '@generated/services/telecom.service';
import { TelecomAccountListItemResult } from '@generated/models/telecom-account-list-item-result';
import { PhoneNumberAgentResult } from '@generated/models/phone-number-agent-result';
import { NotificationService } from '@core/services/notification.service';
import { ImportTwilioNumberComponent } from '../import-twilio-number/import-twilio-number';
import { ImportVoximplantNumberComponent } from '../import-voximplant-number/import-voximplant-number.component';
import { ImportCustomSipNumberComponent } from '../import-custom-sip-number/import-custom-sip-number';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { TagModule } from 'primeng/tag';

@Component({
  selector: 'app-phone-numbers',
  standalone: true,
  imports: [
    ButtonModule,
    TableModule,
    ProgressSpinnerModule,
    TooltipModule,
    MenuModule,
    TagModule,
    ImportTwilioNumberComponent,
    ImportVoximplantNumberComponent,
    ImportCustomSipNumberComponent,
    DeleteConfirmationComponent
],
  templateUrl: './phone-numbers.html',
  styleUrl: './phone-numbers.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneNumbersComponent implements OnInit {
  private telecomService = inject(TelecomService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  accounts: TelecomAccountListItemResult[] = [];
  phoneNumbers: PhoneNumberAgentResult[] = [];
  isLoading = false;

  // Import drawers
  importDrawerVisible = false;
  importVoximplantDrawerVisible = false;
  importCustomSipDrawerVisible = false;

  // Delete dialog
  deleteDialogVisible = false;
  deletingPhoneNumber: PhoneNumberAgentResult | null = null;
  isDeleting = false;

  importMenuItems: MenuItem[] = [
    {
      label: $localize`:@@phoneNumbers.fromTwilio:From Twilio`,
      iconUrl: '/twilio.svg',
      command: () => this.importFromTwilio()
    } as any,
    {
      label: $localize`:@@phoneNumbers.fromVoximplant:From Voximplant`,
      iconUrl: '/voximplant.svg',
      command: () => this.importFromVoximplant()
    } as any,
    {
      label: $localize`:@@phoneNumbers.fromCustomSip:From Custom SIP`,
      icon: 'pi pi-server',
      command: () => this.importFromCustomSip()
    } as any
  ];

  readonly deleteTitle = $localize`:@@phoneNumbers.delete.title:Delete Phone Number`;
  get deleteSubtitle(): string {
    const number = this.deletingPhoneNumber?.phone_number || '';
    return $localize`:@@phoneNumbers.delete.subtitle:Are you sure you want to delete phone number '${number}:number:'? This action cannot be undone.`;
  }

  ngOnInit(): void {
    this.loadAccounts();
  }

  private loadAccounts(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomAccountsGet().subscribe({
      next: (accounts) => {
        this.accounts = accounts || [];
        // Flatten all phone number agents from all accounts
        this.phoneNumbers = this.accounts.flatMap(account =>
          account.phone_number_agents || []
        );
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load telecom accounts:', error);
        this.notificationService.showError($localize`:@@phoneNumbers.notify.loadFail:Failed to load phone numbers`);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  importFromTwilio(): void {
    this.importDrawerVisible = true;
    this.cdr.markForCheck();
  }

  importFromVoximplant(): void {
    this.importVoximplantDrawerVisible = true;
    this.cdr.markForCheck();
  }

  importFromCustomSip(): void {
    this.importCustomSipDrawerVisible = true;
    this.cdr.markForCheck();
  }

  onImported(): void {
    this.loadAccounts();
  }

  get customAccounts(): TelecomAccountListItemResult[] {
    return this.accounts.filter(a => a.provider === 'Custom');
  }

  customAccountSubtitle(account: TelecomAccountListItemResult): string {
    const count = account.phone_number_agents?.length || 0;
    return $localize`:@@phoneNumbers.account.numbersCount:${count}:count: phone numbers`;
  }

  openAccount(account: TelecomAccountListItemResult): void {
    if (account.id) {
      this.router.navigate(['/telecom-accounts', account.id]);
    }
  }

  onCustomSipDrawerVisibleChange(visible: boolean): void {
    this.importCustomSipDrawerVisible = visible;
    this.cdr.markForCheck();
  }

  navigateToAgent(agentId: string | undefined): void {
    if (agentId) {
      this.router.navigate(['/agents', agentId]);
    }
  }

  getProviderForPhoneNumber(phoneNumber: PhoneNumberAgentResult): string {
    const account = this.accounts.find(acc =>
      acc.phone_number_agents?.some(pn => pn.id === phoneNumber.id)
    );
    return this.providerLabel(account?.provider);
  }

  providerLabel(provider: string | undefined): string {
    switch (provider) {
      case 'Custom': return $localize`:@@telecomProvider.custom:Custom SIP`;
      case 'Twilio': return 'Twilio';
      case 'Voximplant': return 'Voximplant';
      default: return provider ?? '';
    }
  }

  editPhoneNumber(phoneNumber: PhoneNumberAgentResult): void {
    if (phoneNumber.id) {
      this.router.navigate(['/phone-numbers', phoneNumber.id]);
    }
  }

  deletePhoneNumber(phoneNumber: PhoneNumberAgentResult): void {
    this.deletingPhoneNumber = phoneNumber;
    this.deleteDialogVisible = true;
    this.cdr.markForCheck();
  }

  onDeleteConfirm(): void {
    if (!this.deletingPhoneNumber?.id) return;

    this.isDeleting = true;
    this.cdr.markForCheck();

    this.telecomService.apiTelecomPhoneNumberAgentsIdDelete({ id: this.deletingPhoneNumber.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@phoneNumbers.notify.deleted:Phone number deleted`);
        this.isDeleting = false;
        this.deleteDialogVisible = false;
        this.deletingPhoneNumber = null;
        this.loadAccounts();
      },
      error: (error) => {
        console.error('Failed to delete phone number:', error);
        this.notificationService.showError($localize`:@@phoneNumbers.notify.deleteFail:Failed to delete phone number`);
        this.isDeleting = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.deleteDialogVisible = false;
    this.deletingPhoneNumber = null;
    this.cdr.markForCheck();
  }

  copyPhoneNumber(phoneNumber: string): void {
    navigator.clipboard.writeText(phoneNumber).then(() => {
      this.notificationService.showSuccess($localize`:@@phoneNumbers.notify.copied:Phone number copied`);
    }).catch((error) => {
      console.error('Failed to copy phone number:', error);
      this.notificationService.showError($localize`:@@phoneNumbers.notify.copyFail:Failed to copy phone number`);
    });
  }

  getMobileMenuItems(phoneNumber: PhoneNumberAgentResult): MenuItem[] {
    return [
      {
        label: $localize`:@@common.edit:Edit`,
        icon: 'pi pi-pencil',
        command: () => this.editPhoneNumber(phoneNumber)
      },
      {
        label: $localize`:@@common.delete:Delete`,
        icon: 'pi pi-trash',
        command: () => this.deletePhoneNumber(phoneNumber)
      }
    ];
  }
}
